/**
 * Phase SC-22c — floor plan, AI layout draft, live occupancy.
 *
 *   GET  /api/seating/floor-plan?branch_id           (404 `seat.floor_plan_missing` until saved)
 *   PUT  /api/seating/floor-plan                     (whole layout; 409 `seat.floor_plan_stale`)
 *   POST /api/seating/floor-plan:generate            (draft only, never saved)
 *   GET  /api/seating/occupancy?date&slot&branch_id  (admin view carries learner names)
 *   WS   /api/seating/ws?branch_id                   (`bearer` subprotocol; seat ids + status only)
 *
 * Units are 10 cm. Shapes mirror `app/domains/seating/schemas.py`
 * (`FloorPlanBody`, `FloorPlanResponse`, `FloorPlanDraftResponse`,
 * `OccupancyResponse`). Floor-plan reads and writes need `manage_zones`.
 */

import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

export type FloorFeatureKind =
  | "entrance"
  | "window"
  | "wall"
  | "ac"
  | "washroom"
  | "reception"
  | "pillar";

export type FloorColorRole =
  | "primary"
  | "secondary"
  | "accent"
  | "success"
  | "warning"
  | "info"
  | "muted";

export interface FloorRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FloorZoneShape extends FloorRect {
  zone_id: string | null;
  label: string;
  color_role: FloorColorRole;
}

export interface FloorFeature extends FloorRect {
  kind: FloorFeatureKind;
  label: string | null;
  rotation: number;
}

export interface FloorSeatPlacement extends FloorRect {
  seat_id: string | null;
  zone_id: string | null;
  zone_label: string | null;
  seat_number: string;
  seat_type: string;
  rotation: number;
}

export interface FloorPlanBody {
  branch_id: string | null;
  name: string;
  width: number;
  height: number;
  grid_size: number;
  zones: FloorZoneShape[];
  features: FloorFeature[];
  seats: FloorSeatPlacement[];
}

export interface FloorPlanResponse extends FloorPlanBody {
  id: string | null;
  version: number;
  updated_at: string | null;
  warnings: string[];
}

export interface SaveFloorPlanInput extends FloorPlanBody {
  create_missing_seats: boolean;
  expected_version: number | null;
}

export interface FloorZoneSpec {
  zone_id?: string | null;
  name: string;
  seat_count: number;
  seat_type?: string;
  notes?: string | null;
}

export interface GenerateFloorPlanInput {
  branch_id?: string | null;
  width?: number | null;
  height?: number | null;
  notes?: string | null;
  zones: FloorZoneSpec[];
  use_ai?: boolean;
}

export type FloorPlanDraftSource = "ai" | "fallback";

export interface FloorPlanDraftResponse {
  plan: FloorPlanBody;
  source: FloorPlanDraftSource;
  fallback_reason: string | null;
  provider: string | null;
  model: string | null;
  warnings: string[];
  new_seat_count: number;
}

export type SeatOccupancyStatus = "free" | "booked" | "occupied" | "fixed" | "blocked";

export interface SeatOccupancyItem {
  seat_id: string;
  seat_number: string;
  zone_id: string;
  status: SeatOccupancyStatus;
  is_mine: boolean;
  booking_id: string | null;
  student_id: string | null;
  learner_name: string | null;
  booking_status: string | null;
}

export interface ZoneOccupancyItem {
  zone_id: string;
  zone_name: string;
  capacity: number;
  taken: number;
}

export interface OccupancyResponse {
  date_key: string;
  slot_key: string | null;
  branch_id: string | null;
  anonymised: boolean;
  seats: SeatOccupancyItem[];
  zones: ZoneOccupancyItem[];
  walk_ins: number;
  room: string;
}

/** Desk footprint the BE grid layout uses (1.2 m × 0.8 m). */
export const DESK_W = 12;
export const DESK_H = 8;

export const FALLBACK_REASON_COPY: Record<string, string> = {
  ai_disabled: "AI layout is turned off for this centre, so this is a grid layout.",
  cost_cap: "Today's AI budget is used up, so this is a grid layout.",
  parse_failed: "The AI reply could not be read, so this is a grid layout.",
  provider_unavailable: "The AI service did not answer, so this is a grid layout.",
  invalid_geometry:
    "The AI layout had seats overlapping or off the floor, so this is a grid layout.",
};

export const OCCUPANCY_STATUS_LABELS: Record<SeatOccupancyStatus, string> = {
  free: "Free",
  booked: "Booked",
  occupied: "Occupied",
  fixed: "Permanent",
  blocked: "Blocked",
};

export const floorPlanKeys = {
  all: ["ia", "seating", "floor-plan"] as const,
  one: (branchId: string | null) => [...floorPlanKeys.all, branchId ?? "default"] as const,
};

export const occupancyKeys = {
  all: ["ia", "seating", "occupancy"] as const,
  one: (dateKey: string, slotKey: string | null, branchId: string | null) =>
    [...occupancyKeys.all, dateKey, slotKey ?? "", branchId ?? ""] as const,
};

function branchQuery(branchId: string | null): string {
  return branchId ? `?branch_id=${encodeURIComponent(branchId)}` : "";
}

/** `null` data means the centre has not saved a floor plan yet. */
export function useFloorPlan(branchId: string | null = null) {
  return useQuery<FloorPlanResponse | null>({
    queryKey: floorPlanKeys.one(branchId),
    queryFn: async () => {
      try {
        return await api.get<FloorPlanResponse>(`/api/seating/floor-plan${branchQuery(branchId)}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 30_000,
  });
}

export function useSaveFloorPlan() {
  const qc = useQueryClient();
  return useMutation<FloorPlanResponse, ApiError, SaveFloorPlanInput>({
    mutationKey: [...floorPlanKeys.all, "save"],
    mutationFn: (body) => api.put<FloorPlanResponse>("/api/seating/floor-plan", body),
    onSuccess: (data) => {
      qc.setQueryData(floorPlanKeys.one(data.branch_id), data);
      void qc.invalidateQueries({ queryKey: ["ia", "seating"] });
    },
  });
}

export interface DeleteFloorPlanInput {
  branchId: string | null;
  /** Version the admin is looking at; a newer save makes the delete fail with 409. */
  expectedVersion: number | null;
}

/** Remove the drawn layout. Rooms, seats, bookings and seat plans stay. */
export function useDeleteFloorPlan() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, DeleteFloorPlanInput>({
    mutationKey: [...floorPlanKeys.all, "delete"],
    mutationFn: ({ branchId, expectedVersion }) => {
      const params = new URLSearchParams();
      if (branchId) params.set("branch_id", branchId);
      if (expectedVersion !== null) params.set("expected_version", String(expectedVersion));
      const query = params.toString();
      return api.delete<void>(`/api/seating/floor-plan${query ? `?${query}` : ""}`);
    },
    onSuccess: (_data, { branchId }) => {
      qc.setQueryData(floorPlanKeys.one(branchId), null);
      void qc.invalidateQueries({ queryKey: ["ia", "seating"] });
    },
  });
}

export function useGenerateFloorPlan() {
  return useMutation<FloorPlanDraftResponse, ApiError, GenerateFloorPlanInput>({
    mutationKey: [...floorPlanKeys.all, "generate"],
    mutationFn: (body) =>
      api.post<FloorPlanDraftResponse>("/api/seating/floor-plan:generate", body),
  });
}

export function useOccupancy(
  dateKey: string,
  slotKey: string | null,
  branchId: string | null,
  options: { pollMs: number | false },
) {
  const search = new URLSearchParams({ date: dateKey });
  if (slotKey) search.set("slot", slotKey);
  if (branchId) search.set("branch_id", branchId);
  return useQuery<OccupancyResponse>({
    queryKey: occupancyKeys.one(dateKey, slotKey, branchId),
    queryFn: () => api.get<OccupancyResponse>(`/api/seating/occupancy?${search.toString()}`),
    enabled: Boolean(dateKey),
    refetchInterval: options.pollMs,
    placeholderData: (previous) => previous,
  });
}

export type RealtimeState = "connecting" | "live" | "polling";

interface SeatStatusFrame {
  type: "seat.status";
  seat_id: string | null;
  status: SeatOccupancyStatus;
  date_key: string | null;
  slot_key: string | null;
  reason: string;
}

export function seatingWebsocketUrl(branchId: string | null): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  const origin = configured?.startsWith("http")
    ? new URL(configured).origin
    : window.location.origin;
  const url = new URL("/api/seating/ws", origin);
  if (branchId) url.searchParams.set("branch_id", branchId);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/**
 * Live seat events. Each event patches the seat's colour in the cached
 * occupancy immediately, then refetches so the admin view picks up names
 * (events never carry them). While the socket is down the caller polls.
 */
export function useSeatingRealtime(branchId: string | null): RealtimeState {
  const qc = useQueryClient();
  const [state, setState] = React.useState<RealtimeState>("connecting");

  React.useEffect(() => {
    const firstToken = api.getAccessToken();
    if (!firstToken || typeof WebSocket === "undefined") {
      setState("polling");
      return;
    }
    let stopped = false;
    let socket: WebSocket | null = null;
    let retry = 800;
    let ping: number | undefined;
    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(seatingWebsocketUrl(branchId), [
        "bearer",
        api.getAccessToken() ?? firstToken,
      ]);
      socket.onopen = () => {
        retry = 800;
        setState("live");
        ping = window.setInterval(() => socket?.send("ping"), 25_000);
      };
      socket.onmessage = (event) => {
        let frame: SeatStatusFrame | { type?: string };
        try {
          frame = JSON.parse(String(event.data)) as SeatStatusFrame | { type?: string };
        } catch {
          return;
        }
        if (frame.type !== "seat.status") return;
        if ((frame as SeatStatusFrame).reason?.startsWith("floor_plan.")) {
          void qc.invalidateQueries({ queryKey: floorPlanKeys.all });
        }
        applySeatFrame(qc, frame as SeatStatusFrame);
        void qc.invalidateQueries({ queryKey: occupancyKeys.all });
      };
      socket.onclose = () => {
        window.clearInterval(ping);
        if (stopped) return;
        setState("polling");
        window.setTimeout(connect, retry);
        retry = Math.min(retry * 1.6, 15_000);
      };
    };
    connect();
    return () => {
      stopped = true;
      window.clearInterval(ping);
      socket?.close();
    };
  }, [qc, branchId]);

  return state;
}

function applySeatFrame(qc: ReturnType<typeof useQueryClient>, frame: SeatStatusFrame): void {
  if (!frame.seat_id) return;
  qc.setQueriesData<OccupancyResponse>({ queryKey: occupancyKeys.all }, (current) => {
    if (!current) return current;
    if (frame.date_key && frame.date_key !== current.date_key) return current;
    if (frame.slot_key && current.slot_key && frame.slot_key !== current.slot_key) return current;
    return {
      ...current,
      seats: current.seats.map((s) =>
        s.seat_id === frame.seat_id && s.status !== "blocked" && s.status !== "fixed"
          ? { ...s, status: frame.status }
          : s,
      ),
    };
  });
}

// ── Geometry helpers (mirror `app/domains/seating/floor_plan.py`) ──────

export function snap(value: number, grid: number): number {
  if (grid <= 0) return value;
  return Math.round(value / grid) * grid;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Axis-aligned bounds of a rect rotated about its centre. */
export function boundingBox(rect: FloorRect, rotation: number): FloorRect {
  const quarter = Math.round(((rotation % 180) + 180) % 180);
  if (quarter === 0) return rect;
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));
  const w = rect.w * cos + rect.h * sin;
  const h = rect.w * sin + rect.h * cos;
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

function intersects(a: FloorRect, b: FloorRect): boolean {
  const eps = 1e-6;
  return (
    a.x < b.x + b.w - eps && b.x < a.x + a.w - eps && a.y < b.y + b.h - eps && b.y < a.y + a.h - eps
  );
}

/** Indexes of seats that overlap another seat or leave the floor. */
export function invalidSeatIndexes(plan: FloorPlanBody): Set<number> {
  const bad = new Set<number>();
  const boxes = plan.seats.map((s) => boundingBox(s, s.rotation));
  boxes.forEach((box, i) => {
    if (box.x < -1e-6 || box.y < -1e-6) bad.add(i);
    if (box.x + box.w > plan.width + 1e-6 || box.y + box.h > plan.height + 1e-6) bad.add(i);
  });
  for (let i = 0; i < boxes.length; i += 1) {
    for (let j = i + 1; j < boxes.length; j += 1) {
      if (intersects(boxes[i]!, boxes[j]!)) {
        bad.add(i);
        bad.add(j);
      }
    }
  }
  return bad;
}

/** The first free spot for a new desk, scanning left-to-right inside `area`. */
export function findFreeSpot(plan: FloorPlanBody, area: FloorRect): { x: number; y: number } {
  const step = Math.max(plan.grid_size, 1);
  const boxes = plan.seats.map((s) => boundingBox(s, s.rotation));
  for (let y = area.y + 2; y + DESK_H <= area.y + area.h; y += step) {
    for (let x = area.x + 2; x + DESK_W <= area.x + area.w; x += step) {
      const candidate = { x, y, w: DESK_W, h: DESK_H };
      if (!boxes.some((b) => intersects(candidate, b))) return { x, y };
    }
  }
  return {
    x: clamp(area.x + 2, 0, plan.width - DESK_W),
    y: clamp(area.y + 2, 0, plan.height - DESK_H),
  };
}

/** Next seat number in a room: `Q-07` after `Q-06`. */
export function nextSeatNumber(plan: FloorPlanBody, zoneLabel: string): string {
  const prefix = (zoneLabel.trim()[0] ?? "S").toUpperCase();
  const taken = new Set(plan.seats.map((s) => s.seat_number));
  for (let n = 1; n < 10_000; n += 1) {
    const candidate = `${prefix}-${String(n).padStart(2, "0")}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${prefix}-${Date.now()}`;
}

export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
